import { useEffect, useState } from "react";
import { LogOut, Settings2, Store } from "lucide-react";
import { toast } from "sonner";
import { vendorApi, formatApiError } from "../lib/api";
import { AGG_ACTIVE_STATUSES } from "../config/defaults";
import { LeafTile } from "../components/Logo";
import { prettyDate, prettySlot } from "../components/AggregatorBookingModal";

const STATUS_TONE = {
  "Booking Requested": "border-amber-400/30 bg-amber-400/10 text-amber-300",
  "Booking Confirmed": "border-carza/30 bg-carza-dim text-carza-lite",
  "Reschedule Requested": "border-carza/30 bg-carza-dim text-carza-lite",
  "Service In Progress": "border-carza/30 bg-carza-dim text-carza-lite",
  "Service Completed": "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  "Cancelled by Customer": "border-white/10 bg-white/5 text-mist-2",
  "Cancelled by Centre": "border-red-400/30 bg-red-400/10 text-red-300",
  "No-Show": "border-red-400/30 bg-red-400/10 text-red-300",
};

export default function VendorPage() {
  const [token, setToken] = useState(() => localStorage.getItem("carza_vendor_token"));
  const [centre, setCentre] = useState(() => null);
  const [tab, setTab] = useState("bookings");

  useEffect(() => {
    if (token) vendorApi.me(token).then((r) => setCentre(r.centre)).catch(() => logout());
  }, []); // eslint-disable-line

  const logout = () => {
    localStorage.removeItem("carza_vendor_token");
    setToken(null); setCentre(null);
  };

  const onLogin = (t, c) => {
    localStorage.setItem("carza_vendor_token", t);
    setToken(t); setCentre(c);
  };

  if (!token || !centre) return <VendorLogin onLogin={onLogin} />;
  return (
    <main className="min-h-screen bg-ink px-5 pb-24 pt-[88px]" data-testid="vendor-page">
      <div className="container-x w-full max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LeafTile size={36} />
            <div>
              <p className="font-display text-xl font-bold text-paper">{centre.name}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist-2">Centre Console · {centre.centre_code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setTab(tab === "bookings" ? "settings" : "bookings")}
              className={`btn px-5 py-2.5 text-[13px] ${tab === "settings" ? "btn-primary" : "btn-outline"}`} data-testid="vendor-tab-toggle">
              {tab === "bookings" ? <><Settings2 size={14} /> Centre Settings</> : "Bookings"}
            </button>
            <button onClick={logout} className="btn btn-ghost px-4 py-2.5 text-[13px]" data-testid="vendor-logout-btn"><LogOut size={14} /> Sign out</button>
          </div>
        </div>

        {tab === "bookings" ? <Bookings token={token} /> : <CentreSettings centre={centre} token={token} onSaved={setCentre} />}
      </div>
    </main>
  );
}

function VendorLogin({ onLogin }) {
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await vendorApi.login(code.trim().toUpperCase(), pin);
      onLogin(res.token, res.centre);
      toast.success("Welcome to your centre console");
    } catch (err) {
      setError(formatApiError(err, "Invalid centre code or PIN"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-ink px-5" data-testid="vendor-login-page">
      <form onSubmit={submit} className="glass flex w-full max-w-sm flex-col gap-4 rounded-3xl p-8" data-testid="vendor-login-form">
        <div className="flex items-center gap-3">
          <LeafTile size={34} />
          <div>
            <p className="font-logo text-sm tracking-[0.18em] text-paper">CARZA</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-mist-2">Centre Console</p>
          </div>
        </div>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Centre code</span>
          <input className="field" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required placeholder="e.g. CARZA119" data-testid="vendor-code-input" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">PIN</span>
          <input className="field" type="password" value={pin} onChange={(e) => setPin(e.target.value)} required placeholder="••••••••" autoComplete="current-password" data-testid="vendor-pin-input" />
        </label>
        {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="vendor-login-error">{error}</p>}
        <button type="submit" disabled={loading} className="btn btn-primary w-full py-3.5 disabled:opacity-50" data-testid="vendor-login-submit">
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </main>
  );
}

function Bookings({ token }) {
  const [bookings, setBookings] = useState(null);
  const [prompt, setPrompt] = useState(null); // {ref, action, title, needReason}

  const load = () => vendorApi.bookings(token).then((r) => setBookings(r.bookings || [])).catch(() => setBookings([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const act = async (ref, action, body) => {
    try {
      await vendorApi.action(ref, action, token, body);
      toast.success("Booking updated");
      setPrompt(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mt-8 flex flex-col gap-3" data-testid="vendor-bookings">
      {!bookings && <p className="py-10 text-center text-sm text-mist-2">Loading bookings…</p>}
      {bookings && bookings.length === 0 && <p className="py-10 text-center text-sm text-mist-2">No bookings yet. Bookings from your centre will appear here.</p>}
      {bookings && bookings.map((b) => (
        <BookingRow key={b.booking_ref} b={b} onAction={(action, title, needReason) => setPrompt({ ref: b.booking_ref, action, title, needReason })} onDecision={act} />
      ))}
      {prompt && (
        <ReasonPrompt prompt={prompt} onClose={() => setPrompt(null)}
          onSubmit={(reason) => act(prompt.ref, prompt.action, reason != null ? { reason } : undefined)} />
      )}
    </div>
  );
}

function BookingRow({ b, onAction, onDecision }) {
  const [showHistory, setShowHistory] = useState(false);
  const canCancel = AGG_ACTIVE_STATUSES.includes(b.status);
  return (
    <div className="card-surface rounded-2xl p-5" data-testid={`vendor-booking-${b.booking_ref}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] tracking-wider text-mist-2">{b.booking_ref}</p>
          <p className="mt-1 text-[15px] font-bold text-paper">{b.customer_name} · +91 {b.customer_phone}</p>
          <p className="mt-0.5 text-[13px] text-mist">{b.service} · {b.vehicle_category}{b.vehicle_number ? ` · ${b.vehicle_number}` : ""}</p>
          <p className="mt-0.5 text-[13px] text-carza-lite">{prettyDate(b.date)}, {prettySlot(b.time_slot)}</p>
          {b.notes && <p className="mt-1 text-[12px] text-mist-2">Notes: {b.notes}</p>}
          <p className="mt-1.5 text-[11px] text-mist-2">Payment: collected by the centre after service</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`rounded-full border px-3 py-1.5 font-mono text-[9px] uppercase tracking-widest ${STATUS_TONE[b.status] || ""}`}>{b.status}</span>
          <div className="flex flex-wrap justify-end gap-2">
            {b.status === "Booking Requested" && (
              <>
                <button onClick={() => onAction("confirm")} className="btn btn-primary px-4 py-2 text-[12px]" data-testid={`vendor-accept-${b.booking_ref}`}>Accept</button>
                <button onClick={() => onAction("reject", "Reject booking", true)} className="btn btn-ghost px-4 py-2 text-[12px] text-red-300" data-testid={`vendor-reject-${b.booking_ref}`}>Reject</button>
              </>
            )}
            {b.status === "Reschedule Requested" && (
              <>
                <button onClick={() => onDecision(b.booking_ref, "reschedule-decision", { approve: true })} className="btn btn-primary px-4 py-2 text-[12px]" data-testid={`vendor-reschedule-approve-${b.booking_ref}`}>Approve Reschedule</button>
                <button onClick={() => onDecision(b.booking_ref, "reschedule-decision", { approve: false })} className="btn btn-ghost px-4 py-2 text-[12px]" data-testid={`vendor-reschedule-reject-${b.booking_ref}`}>Reject Reschedule</button>
              </>
            )}
            {b.status === "Booking Confirmed" && (
              <button onClick={() => onAction("start")} className="btn btn-primary px-4 py-2 text-[12px]" data-testid={`vendor-start-${b.booking_ref}`}>Start Service</button>
            )}
            {b.status === "Service In Progress" && (
              <button onClick={() => onAction("complete")} className="btn btn-primary px-4 py-2 text-[12px]" data-testid={`vendor-complete-${b.booking_ref}`}>Mark Completed</button>
            )}
            {canCancel && b.status !== "Service In Progress" && (
              <button onClick={() => onAction("cancel", "Cancel booking (centre)", true)} className="btn btn-ghost px-4 py-2 text-[12px] text-red-300" data-testid={`vendor-cancel-${b.booking_ref}`}>Cancel</button>
            )}
            {(b.status === "Booking Confirmed" || b.status === "Booking Requested") && (
              <button onClick={() => onAction("noshow", "Report no-show", true)} className="btn btn-ghost px-4 py-2 text-[12px] text-amber-300" data-testid={`vendor-noshow-${b.booking_ref}`}>No-Show</button>
            )}
          </div>
        </div>
      </div>
      {(b.reschedule_history || []).length > 0 && (
        <p className="mt-3 border-t border-white/5 pt-2 text-[11px] text-mist-2">
          Rescheduled: {(b.reschedule_history || []).map((h) => `${h.from_date} ${prettySlot(h.from_slot)} → ${h.to_date} ${prettySlot(h.to_slot)}`).join(" | ")}
        </p>
      )}
      <button onClick={() => setShowHistory((v) => !v)} className="mt-2 font-mono text-[10px] uppercase tracking-widest text-mist-2 hover:text-paper" data-testid={`vendor-history-toggle-${b.booking_ref}`}>
        {showHistory ? "Hide" : "Show"} audit trail
      </button>
      {showHistory && (
        <div className="mt-2 flex flex-col gap-1.5 border-l border-carza/30 pl-3" data-testid={`vendor-history-${b.booking_ref}`}>
          {(b.audit || []).map((a, i) => (
            <p key={i} className="text-[11px] leading-relaxed text-mist">
              <span className="text-mist-2">{(a.at || "").slice(0, 16).replace("T", " ")}</span> · {a.actor} · {a.action}{a.detail ? ` — ${a.detail}` : ""} ({a.prev_status || "—"} → {a.new_status})
            </p>
          ))}
          {(b.notifications || []).map((n, i) => (
            <p key={`n${i}`} className="text-[11px] text-mist-2">
              Notification ({n.channel}{n.delivered ? ", delivered" : ", shown in console only — no external channel configured"}): {n.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function ReasonPrompt({ prompt, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const submit = () => {
    if (prompt.needReason && reason.trim().length < 3) return setError("Please enter a reason (min 3 characters)");
    onSubmit(prompt.needReason ? reason.trim() : null);
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-5" onClick={onClose}>
      <div className="glass w-full max-w-sm rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="vendor-reason-prompt">
        <h3 className="font-display text-lg font-bold text-paper">{prompt.title}</h3>
        <input className="field mt-4" value={reason} onChange={(e) => { setReason(e.target.value); setError(""); }}
          placeholder="Reason (recorded for operations)" autoFocus data-testid="vendor-reason-input" />
        {error && <p className="mt-2 text-xs text-red-300" data-testid="vendor-reason-error">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-ghost px-4 py-2.5 text-[13px]">Back</button>
          <button onClick={submit} className="btn btn-primary px-5 py-2.5 text-[13px]" data-testid="vendor-reason-submit">Confirm</button>
        </div>
      </div>
    </div>
  );
}

const SLOT_OPTIONS = [];
for (let h = 6; h <= 21; h++) SLOT_OPTIONS.push(`${String(h).padStart(2, "0")}:00`);

function CentreSettings({ centre, token, onSaved }) {
  const [form, setForm] = useState({
    services: (centre.services || []).join(", "),
    vehicle_categories: (centre.vehicle_categories || []).join(", "),
    slot_hours: centre.slot_hours || [],
    slot_capacity: centre.slot_capacity || 2,
    booking_days_ahead: centre.booking_days_ahead || 7,
    booking_mode: centre.booking_mode || "auto",
    phone: centre.phone || "",
    address: centre.address || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleHour = (h) => {
    const cur = form.slot_hours || [];
    set("slot_hours", cur.includes(h) ? cur.filter((x) => x !== h) : [...cur, h].sort());
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        vehicle_categories: form.vehicle_categories.split(",").map((s) => s.trim()).filter(Boolean),
        slot_hours: form.slot_hours,
        slot_capacity: Number(form.slot_capacity),
        booking_days_ahead: Number(form.booking_days_ahead),
        booking_mode: form.booking_mode,
        phone: form.phone,
        address: form.address,
      };
      const res = await vendorApi.saveConfig(payload, token);
      onSaved(res.centre);
      toast.success("Centre settings saved");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass mt-8 flex flex-col gap-5 rounded-3xl p-6" data-testid="vendor-settings">
      <label className="flex flex-col gap-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Services offered (comma separated)</span>
        <input className="field" value={form.services} onChange={(e) => set("services", e.target.value)} data-testid="vendor-services-input" />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Vehicle categories accepted (comma separated)</span>
        <input className="field" value={form.vehicle_categories} onChange={(e) => set("vehicle_categories", e.target.value)} data-testid="vendor-vehicles-input" />
      </label>
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Slot hours (leave empty to show “Availability to be confirmed”)</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {SLOT_OPTIONS.map((h) => (
            <button key={h} onClick={() => toggleHour(h)}
              className={`rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-all ${(form.slot_hours || []).includes(h) ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist"}`}
              data-testid={`vendor-hour-${h.replace(":", "-")}`}>{prettySlot(h)}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Vehicles per slot</span>
          <input className="field" type="number" min="1" max="20" value={form.slot_capacity} onChange={(e) => set("slot_capacity", e.target.value)} data-testid="vendor-capacity-input" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Booking days ahead</span>
          <input className="field" type="number" min="1" max="30" value={form.booking_days_ahead} onChange={(e) => set("booking_days_ahead", e.target.value)} data-testid="vendor-days-input" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Booking acceptance</span>
          <select className="field" value={form.booking_mode} onChange={(e) => set("booking_mode", e.target.value)} data-testid="vendor-mode-select">
            <option value="auto">Auto-confirm slots</option>
            <option value="manual">Manual review (I accept each booking)</option>
          </select>
        </label>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Contact number shown to customers</span>
          <input className="field" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="vendor-phone-input" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Address</span>
          <input className="field" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="vendor-address-input" />
        </label>
      </div>
      <p className="flex items-start gap-2 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 text-[12px] leading-relaxed text-mist" data-testid="vendor-rates-note">
        <Store size={14} className="mt-0.5 shrink-0 text-carza-lite" />
        Service rates are set and collected by your centre directly — CARZA does not display or collect pricing for CARZA Nearby bookings.
      </p>
      {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="vendor-settings-error">{error}</p>}
      <button onClick={save} disabled={saving} className="btn btn-primary w-full py-3.5 disabled:opacity-50" data-testid="vendor-settings-save">
        {saving ? "Saving…" : "Save Centre Settings"}
      </button>
    </div>
  );
}
